import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  GraduationCap, Crown, Sparkles, FileCheck2, Tag, Users2, Home, Briefcase, Shield, ArrowRight, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import GoogleLoginButton from "@/components/GoogleLoginButton";

const FEATURES = [
  {
    icon: FileCheck2,
    label: "Démarches simplifiées",
    sub: "CAF, CROUS, Ameli, Carte d'identité…",
    color: "from-blue-500 to-blue-600",
    testid: "feat-demarches",
  },
  {
    icon: Sparkles,
    label: "Assistant IA",
    sub: "CV, lettre de motivation, entretien",
    color: "from-violet-500 to-violet-600",
    testid: "feat-ia",
  },
  {
    icon: Home,
    label: "Logement & jobs",
    sub: "Studios, alternance, jobs étudiants",
    color: "from-cyan-500 to-cyan-600",
    testid: "feat-housing",
  },
  {
    icon: Tag,
    label: "Bons plans",
    sub: "Réductions exclusives & SNCF Avantage",
    color: "from-amber-500 to-orange-500",
    testid: "feat-deals",
  },
  {
    icon: Users2,
    label: "Communauté locale",
    sub: "Forum par ville, entraide étudiants",
    color: "from-emerald-500 to-emerald-600",
    testid: "feat-community",
  },
];

export default function Login() {
  const { login, loginWith2FA } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [premiumCount, setPremiumCount] = useState(null);
  const [twoFa, setTwoFa] = useState({ active: false, challenge_token: "", code: "" });

  useEffect(() => {
    api.get("/premium/stats").then((r) => setPremiumCount(r.data.active)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok && res.requires_2fa) {
      setTwoFa({ active: true, challenge_token: res.challenge_token, code: "" });
      toast.info("Saisis le code 2FA de ton application d'authentification");
      return;
    }
    if (res.ok) {
      toast.success("Bienvenue sur Student Hub");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Connexion échouée");
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    if (!twoFa.code || twoFa.code.replace(/\s|-/g, "").length < 6) {
      toast.error("Code à 6 chiffres requis (ou code de secours)");
      return;
    }
    setLoading(true);
    const res = await loginWith2FA(twoFa.challenge_token, twoFa.code);
    setLoading(false);
    if (res.ok) {
      toast.success("Connexion sécurisée");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Code 2FA invalide");
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-white" data-testid="hero-landing">
      {/* Subtle bg accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[32rem] h-[32rem] rounded-full bg-violet-200/40 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[20rem] h-[20rem] rounded-full bg-cyan-100/50 blur-3xl" />
      </div>

      {/* Top nav */}
      <header className="relative z-10 px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="hero-logo">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center shadow-lg shadow-violet-500/25 group-hover:scale-105 transition-transform">
            <GraduationCap size={22} />
          </div>
          <span className="font-display text-xl font-bold tracking-tight text-slate-900">Student Hub</span>
        </Link>
        <button
          onClick={() => setShowLogin(true)}
          className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100 transition-all"
          data-testid="nav-login-btn"
        >
          Se connecter <ArrowRight size={14} />
        </button>
      </header>

      {/* Main hero */}
      <main className="relative z-10 px-6 sm:px-10 lg:px-16 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center max-w-7xl mx-auto pt-4 lg:pt-8">
          {/* Left — text + CTAs */}
          <div className="lg:col-span-6 space-y-7" data-testid="hero-content">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-xs font-medium text-violet-700">
              <Sparkles size={12} className="text-violet-600" />
              Powered by Claude IA · Made in France 🇫🇷
            </div>

            <h1
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.0] tracking-tight text-slate-900"
              data-testid="hero-title"
            >
              Réussis ton{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                  avenir
                </span>
                <span className="absolute -bottom-1 left-0 right-0 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 opacity-80"></span>
              </span>
              <br />étudiant
            </h1>

            <p className="text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              L'application tout-en-un pour gérer tes <strong className="text-slate-900">démarches administratives</strong>,
              ton <strong className="text-slate-900">logement</strong>, ton <strong className="text-slate-900">job</strong> et
              profiter pleinement de la vie étudiante.
            </p>

            {/* Feature list with colored icon squares */}
            <ul className="space-y-3 pt-2" data-testid="features-list">
              {FEATURES.slice(0, 3).map((f) => {
                const Icon = f.icon;
                return (
                  <li key={f.label} className="flex items-center gap-3" data-testid={f.testid}>
                    <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${f.color} flex items-center justify-center shadow-md shrink-0`}>
                      <Icon size={20} className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-sm">{f.label}</p>
                      <p className="text-xs text-slate-500">{f.sub}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Multilingue badge — rassure les étudiants internationaux / Erasmus */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 text-xs font-medium text-emerald-800 shadow-sm"
              data-testid="hero-multilingue-badge"
              title="Chat communautaire et annonces traduisibles à la volée dans 8 langues"
            >
              <span aria-hidden="true">🌐</span>
              <span className="font-semibold">Multilingue</span>
              <span className="text-emerald-700/80">· FR · EN · ES · AR · 中文 · DE · IT · PT</span>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                asChild
                className="h-13 px-8 py-4 rounded-full text-base font-semibold bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-xl shadow-violet-600/30 hover:shadow-violet-600/50 hover:scale-[1.02] active:scale-100 transition-all border-0 text-white"
                data-testid="cta-register"
              >
                <Link to="/register">
                  Commencer gratuitement
                  <ArrowRight size={16} className="ml-1" />
                </Link>
              </Button>
              <button
                onClick={() => setShowLogin(true)}
                className="h-13 px-8 py-4 rounded-full text-base font-semibold text-slate-700 bg-white hover:bg-slate-50 border-2 border-slate-200 hover:border-slate-300 transition-all"
                data-testid="cta-login"
              >
                Se connecter
              </button>
            </div>

            {/* (Multilingue badge intentionally placed ABOVE the CTAs — see block above) */}

            {/* Social proof */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-3 text-xs text-slate-500">
              {premiumCount !== null && premiumCount > 0 && (
                <div className="flex items-center gap-1.5" data-testid="premium-social-proof">
                  <Crown size={14} className="text-yellow-500" />
                  <span><strong className="text-slate-900">{premiumCount}+</strong> étudiant{premiumCount > 1 ? "s" : ""} Premium</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Shield size={14} className="text-emerald-500" />
                <span>RGPD · Données chiffrées</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Briefcase size={14} className="text-blue-500" />
                <span>Auto-entrepreneur déclaré</span>
              </div>
            </div>
          </div>

          {/* Right — Hero card with photo */}
          <div className="lg:col-span-6 relative" data-testid="hero-image-card">
            <div className="relative rounded-[32px] overflow-hidden shadow-2xl shadow-violet-300/40 ring-1 ring-slate-200/50">
              {/* Image */}
              <div
                className="aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?auto=format&fit=crop&w=1400&q=85')",
                }}
              />
              {/* Subtle gradient bottom */}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
              {/* Confetti dots */}
              <div className="absolute inset-0 pointer-events-none">
                <span className="absolute top-10 left-10 w-2 h-2 rounded-full bg-yellow-300 animate-pulse" />
                <span className="absolute top-24 right-16 w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" style={{ animationDelay: "0.6s" }} />
                <span className="absolute top-1/3 left-20 w-2 h-2 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: "1.2s" }} />
                <span className="absolute bottom-32 right-10 w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: "1.8s" }} />
              </div>

              {/* Floating stats card top-right */}
              <div className="hidden md:flex absolute top-5 right-5 items-center gap-2 rounded-2xl bg-white/95 backdrop-blur px-3.5 py-2.5 shadow-xl ring-1 ring-slate-200/50">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center shrink-0">
                  <Shield size={14} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Sécurité</p>
                  <p className="text-xs font-bold text-slate-900">RGPD · 🇫🇷 Hébergé</p>
                </div>
              </div>

              {/* Glass card overlay — bottom */}
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 backdrop-blur-xl p-4 shadow-2xl ring-1 ring-slate-200/50">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shrink-0 shadow-lg shadow-violet-500/30">
                    <GraduationCap size={20} className="text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-semibold text-sm text-slate-900 truncate">Tout pour ta vie étudiante</p>
                    <p className="text-[11px] text-slate-500 truncate">CAF · CROUS · Logement · Jobs · IA · Communauté</p>
                  </div>
                  <Sparkles size={16} className="text-violet-500 shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust band */}
        <div className="max-w-7xl mx-auto mt-16 pt-10 border-t border-slate-200/70">
          <p className="text-center text-xs uppercase tracking-[0.25em] font-semibold text-slate-400 mb-6">
            Tout ce dont tu as besoin, en un seul endroit
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.label}
                  className="flex flex-col items-center text-center p-4 rounded-2xl bg-white/60 backdrop-blur border border-slate-100 hover:border-violet-200 hover:shadow-md transition-all"
                  data-testid={`trust-${f.testid}`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-2 shadow`}>
                    <Icon size={18} className="text-white" />
                  </div>
                  <p className="text-xs font-semibold text-slate-900">{f.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Login modal */}
      {showLogin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setShowLogin(false)}
          data-testid="login-modal-backdrop"
        >
          <div
            className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200/50 p-7 sm:p-9"
            onClick={(e) => e.stopPropagation()}
            data-testid="login-modal"
          >
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center">
                <GraduationCap size={18} />
              </div>
              <span className="font-display text-lg font-bold text-slate-900">Student Hub</span>
            </div>
            <h2 className="font-display text-2xl font-bold mb-1 text-slate-900">
              {twoFa.active ? "Vérification en 2 étapes" : "Content de te revoir"}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {twoFa.active
                ? "Saisis le code à 6 chiffres de ton application d'authentification (Google Authenticator, 1Password, Authy...). Tu peux aussi utiliser un code de secours."
                : "Connecte-toi pour accéder à ton espace."}
            </p>
            {twoFa.active ? (
              <form onSubmit={submit2FA} className="space-y-4" data-testid="login-2fa-form">
                <div>
                  <Label htmlFor="2fa-code" className="text-slate-700 text-xs">Code 2FA</Label>
                  <Input
                    id="2fa-code"
                    inputMode="text"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    value={twoFa.code}
                    onChange={(e) => setTwoFa((s) => ({ ...s, code: e.target.value }))}
                    placeholder="123 456 ou XXXXX-XXXXX"
                    data-testid="login-2fa-code-input"
                    className="mt-1.5 tracking-widest text-center font-mono text-lg"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-lg shadow-violet-600/30 border-0"
                  data-testid="login-2fa-submit-btn"
                >
                  {loading ? "Vérification..." : "Vérifier"}
                </Button>
                <button
                  type="button"
                  onClick={() => setTwoFa({ active: false, challenge_token: "", code: "" })}
                  className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
                  data-testid="login-2fa-cancel"
                >
                  Retour à la connexion
                </button>
              </form>
            ) : (
            <>
              <GoogleLoginButton testid="login-google-btn" />
              <div className="my-5 flex items-center gap-3" data-testid="login-divider">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-xs text-slate-400 uppercase tracking-wider">ou</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-slate-700 text-xs">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tonemail@etudiant.fr"
                  data-testid="login-email-input"
                  className="mt-1.5"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700 text-xs">Mot de passe</Label>
                  <Link to="/forgot-password" className="text-xs text-violet-600 hover:underline" data-testid="forgot-password-link">
                    Mot de passe oublié ?
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="mt-1.5"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-lg shadow-violet-600/30 border-0"
                data-testid="login-submit-btn"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>
            </>
            )}
            <p className="text-xs text-slate-500 mt-5 text-center">
              Pas encore de compte ?{" "}
              <Link to="/register" className="text-violet-600 font-medium hover:underline" data-testid="go-register-link">
                Créer un compte
              </Link>
            </p>
            <button
              type="button"
              onClick={() => setShowLogin(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition"
              aria-label="Fermer"
              data-testid="login-modal-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
